import * as React from "react"
import { format, parse } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/**
 * DatePicker - The standard date input for the entire app.
 *
 * ⚠️ DO NOT use native `<input type="date">` anywhere in the codebase.
 * The native control has known focus/click-through bugs inside modals and
 * drawers (the OS-level picker overlay intercepts clicks on neighbouring
 * fields), and its visual styling does not match the rest of the UI.
 *
 * Always use this `<DatePicker>` instead. Examples:
 *   <DatePicker
 *     value={form.start_date}
 *     onChange={(d) => setForm(prev => ({ ...prev, start_date: d }))}
 *     formatDisplay={formatDateDisplay}    // optional
 *     minDate={form.start_date}            // optional
 *     className="w-full"
 *     testId="job-start-date"
 *   />
 *
 * @param {Object} props
 * @param {string} props.value - Date value in 'yyyy-MM-dd' format
 * @param {function} props.onChange - Callback when date changes, receives 'yyyy-MM-dd' string
 * @param {string} props.placeholder - Placeholder text when no date selected
 * @param {function} props.formatDisplay - Optional function to format the displayed date
 * @param {function} props.disabled - Optional function to disable specific dates
 * @param {string} props.minDate - Optional minimum date in 'yyyy-MM-dd' format
 * @param {string} props.maxDate - Optional maximum date in 'yyyy-MM-dd' format
 * @param {string} props.className - Optional additional className for the trigger button
 * @param {string} props.testId - Optional data-testid for testing
 * @param {boolean} props.isDisabled - Optional flag to disable the entire picker
 */
function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  formatDisplay,
  disabled,
  minDate,
  maxDate,
  className,
  testId,
  isDisabled = false,
  captionLayout,
  fromYear,
  toYear,
}) {
  const [open, setOpen] = React.useState(false)

  // Parse value to Date object
  const selectedDate = React.useMemo(() => {
    if (!value) return undefined
    try {
      return parse(value, 'yyyy-MM-dd', new Date())
    } catch {
      return undefined
    }
  }, [value])

  // Handle date selection
  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'))
      setOpen(false)
    }
  }

  // Default disabled function based on minDate/maxDate
  const disabledFn = React.useMemo(() => {
    if (disabled) return disabled
    
    return (date) => {
      if (minDate) {
        const min = parse(minDate, 'yyyy-MM-dd', new Date())
        if (date < min) return true
      }
      if (maxDate) {
        const max = parse(maxDate, 'yyyy-MM-dd', new Date())
        if (date > max) return true
      }
      return false
    }
  }, [disabled, minDate, maxDate])

  // Display text
  const displayText = React.useMemo(() => {
    if (!value) return placeholder
    if (formatDisplay) return formatDisplay(value)
    return value
  }, [value, placeholder, formatDisplay])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={isDisabled}
          className={cn(
            "justify-start text-left font-mono h-9",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={disabledFn}
          captionLayout={captionLayout}
          fromYear={fromYear}
          toYear={toYear}
          defaultMonth={selectedDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

DatePicker.displayName = "DatePicker"

export { DatePicker }
